import type { Snapshot } from '../shared/contract';
import { DT } from '../core/const';
import type { RunDirector } from '../core/run';

/**
 * THE LOOP — fixed-step sim, interpolated render.
 *
 * The sim runs at a fixed 120 Hz and the renderer runs at whatever the display gives us.
 * These are different clocks on purpose (architecture.md), and the accumulator below is
 * the only place they meet.
 *
 * Two rules that determinism depends on, both easy to break by accident:
 *
 * 1. **The sim never sees a variable dt.** `DT` is a constant; a frame that took 40 ms
 *    runs five sim steps, it does not run one big one. The moment wall-clock time reaches
 *    the physics, a run stops being reproducible and the replay, the daily seed and the
 *    FOTOAUTOMAT all die with it.
 * 2. **A slow frame drops sim time rather than spiralling.** If the tab is backgrounded
 *    for a minute we do not then simulate a minute of arcade — we clamp. This makes the
 *    *live* session diverge from a strict replay of the same input log, which is the
 *    correct trade: a replay re-runs every tick from the log at full fidelity, while a
 *    live player who alt-tabbed does not deserve a frozen browser. The clamp is logged in
 *    `droppedTicks` so it is visible rather than silent.
 */

export interface LoopStats {
  fps: number;
  simTicks: number;
  stepMs: number;
  renderMs: number;
  droppedTicks: number;
}

/** Never simulate more than this many ticks in one frame (0.25 s of arcade). */
const MAX_STEPS_PER_FRAME = 30;

export class GameLoop {
  readonly stats: LoopStats = { fps: 0, simTicks: 0, stepMs: 0, renderMs: 0, droppedTicks: 0 };

  /**
   * **The only slow-motion in this game.** ART-BIBLE §7.1 names `SLOWMO_SCALE = 0.25`;
   * Die Lehrnacht spends it exactly once, on the first payout of a player's life, so the
   * arithmetic can be read stage by stage. Nothing else may set this.
   *
   * It scales *wall-clock time*, never `DT`. The sim still runs at a fixed 120 Hz and
   * still sees a constant step — it simply runs fewer steps per real second, which is
   * indistinguishable from a slow computer and therefore cannot affect determinism. A
   * replay re-runs every tick from the log at full fidelity regardless.
   */
  timescale = 1;

  private acc = 0;
  private last = 0;
  private raf = 0;
  private running = false;
  private fpsAcc = 0;
  private fpsFrames = 0;

  constructor(
    private readonly dir: RunDirector,
    private readonly onFrame: (s: Snapshot, dt: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const tick = (now: number): void => {
      if (!this.running) return;
      this.frame(now);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Drive one frame by hand. The E2E harness uses this so screenshots are reproducible. */
  frame(now: number): Snapshot {
    const real = Math.min(0.25, Math.max(0, (now - this.last) / 1000));
    // scale before the clamp is applied to the accumulator, and hand the SAME scaled dt
    // to the renderer — a slow-motion sim with a full-speed diorama is two clocks
    const wall = real * this.timescale;
    this.last = now;
    this.acc += wall;

    const t0 = performance.now();
    let steps = 0;
    while (this.acc >= DT && steps < MAX_STEPS_PER_FRAME) {
      this.dir.step();
      this.acc -= DT;
      steps++;
    }
    if (this.acc >= DT) {
      // we are behind: throw the backlog away rather than chase it forever
      this.stats.droppedTicks += Math.floor(this.acc / DT);
      this.acc = 0;
    }
    const t1 = performance.now();

    // ONE snapshot per frame, always — it is also what drains the event feed, so skipping
    // it on a frame where nothing stepped would make events pile up and arrive in a burst.
    const snap = this.dir.snapshot();
    this.onFrame(snap, wall);
    const t2 = performance.now();

    this.stats.simTicks = steps;
    this.stats.stepMs = t1 - t0;
    this.stats.renderMs = t2 - t1;
    this.fpsAcc += wall;
    this.fpsFrames++;
    if (this.fpsAcc >= 0.5) {
      this.stats.fps = Math.round(this.fpsFrames / this.fpsAcc);
      this.fpsAcc = 0;
      this.fpsFrames = 0;
    }
    return snap;
  }

  /** Advance a fixed number of sim ticks with no wall clock at all (tests, replays). */
  advance(ticks: number): Snapshot {
    for (let i = 0; i < ticks; i++) this.dir.step();
    const snap = this.dir.snapshot();
    this.onFrame(snap, ticks * DT);
    return snap;
  }
}
